import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { SubSink } from 'subsink';
import { AuthApiService } from '../../apiCalls/auth-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { passwordsMatchValidator, strongPasswordValidator } from '../../shared/password-validator';
import { AuthService } from '../auth.service';
import { GetToken, LoginFormValue } from '../login/login';

interface ResetPasswordForm {
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
}

export interface ResetPasswordFormValue {
  newPassword: string;
  confirmPassword: string;
  email: string;
  token: string;
}

@Component({
  selector: 'app-reset-password',
  imports: [MatButtonModule, MatFormField, MatInputModule, ReactiveFormsModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnDestroy {
  private readonly subs = new SubSink();

  // Injected dependencies
  private readonly authApiSvc = inject(AuthApiService);
  private readonly authSvc = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  /** Form definition */
  form: FormGroup<ResetPasswordForm> = this.fb.nonNullable.group(
    {
      newPassword: ['', [Validators.required, strongPasswordValidator()]],
      confirmPassword: ['', [Validators.required, strongPasswordValidator()]],
    },
    {
      validators: passwordsMatchValidator('newPassword', 'confirmPassword'),
    },
  );

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();

    const value: ResetPasswordFormValue = {
      ...formValue,
      email: this.route.snapshot.queryParamMap.get('email') || '',
      token: this.route.snapshot.queryParamMap.get('token') || '',
    };

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.authApiSvc.resetPassword(value).subscribe({
      next: () => this.handleSuccess(value),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(value: ResetPasswordFormValue): void {
    this.clearErrorMessage();
    this.saveState.set(false);

    const loginFormValue = {
      email: value.email,
      password: value.newPassword,
    } as LoginFormValue;

    this.subs.sink = this.authApiSvc.loginApiCall(loginFormValue).subscribe({
      next: (c) => this.loginSuccess(c, loginFormValue),
      error: (error) => this.handleError(error),
    });
  }

  /**
   * After Reset Password, automatically log the user in
   */
  private loginSuccess(token: GetToken, loginFormValue: LoginFormValue) {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.authSvc.login(loginFormValue, token);
    this.customSnackbarSvc.successSnackbar('Password changed successfully and logged in.');
  }

  /**
   * Handles errors
   */
  private handleError(error: any): void {
    const errorMessage = error?.message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  get newPassword() {
    return this.form.controls.newPassword;
  }
  get confirmPassword() {
    return this.form.controls.confirmPassword;
  }
}
